// Shared "is a call live right now" flag, set by whichever of the three dialer surfaces
// (RtHubDialerProvider, SalesPowerDialerPage, VaDialerPage) currently has one in progress.
//
// ⛔ 28 Sep 2026: AutoUpdate.tsx reloads the tab once a new deploy is detected AND the tab is
// hidden — deliberately not while visible, so nobody loses in-progress work (see its own header).
// But a phone call keeps running while the tab is backgrounded (WebRTC audio doesn't need focus),
// and a rep alt-tabbing mid-call is completely normal — so "hidden" was never a safe proxy for "not
// doing anything important" for a call specifically. Reported live: Sladen, two calls cut mid-call
// ("it forces me to reload the page and it then cuts the call and ends it") — on a day with roughly
// one deploy every 15 minutes, the 5-minute-window odds of "a new build landed AND the tab went
// hidden while on a call" stopped being negligible. A reload is unconditionally fatal to an active
// Twilio Device/Call (a full page load tears down all JS state), so this is a plain module-scoped
// flag rather than a React context/event — AutoUpdate has no dialer awareness and shouldn't need
// any, it just asks a yes/no question before doing something destructive.
//
// ⛔ 29 Sep 2026: extended to broadcast this SAME flag across browser tabs, rather than build a
// second, independently-drifting "is anyone busy" signal. Each of the three dialer surfaces builds
// its own Device, and Twilio rings every registered Device for an identity — a sibling tab's own
// ring plays at full volume over a call live in another tab, since that tab correctly has no active
// call of its own. Reported live: Anestasia, 29 Sep 2026 — "it rings over the call I'm already
// on... quite loud". See crossTabBusy.logic.ts for the pure set logic this wraps.
import { BUSY_HEARTBEAT_MS, isAnyOtherTabBusy, parseAnsweredCallSid, parseBusyTabs, serializeBusyTabs, withBusyTab, withIdleTab, type BusyTabs } from './crossTabBusy.logic';

let active = false;

// ⛔ 13 Oct 2026: a NEW key, not the old one reformatted. The old value (a bare list of tab ids, no times) can
// hold an id from a tab that died long ago, and nothing could ever tell; by reading a different key every such
// leftover is ignored the moment this build loads, on every machine. A tab still running the previous build
// keeps writing the old key and so is not seen as busy by this one until it reloads — hours at most.
export const BUSY_TABS_KEY = 'rt-hub-busy-tabs-v2';
const CALL_ANSWERED_KEY = 'rt-hub-call-answered';
// Stable for this tab's whole lifetime. crypto.randomUUID() is available in every browser RT Hub
// targets (a secure-context Chromium build); the fallback only matters for an environment without it.
const tabId =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

function readBusyTabs(): BusyTabs {
  try {
    return parseBusyTabs(localStorage.getItem(BUSY_TABS_KEY));
  } catch {
    return new Map(); // private window / blocked storage — cross-tab muting silently does nothing
  }
}

function writeBusyTabs(tabs: BusyTabs): void {
  try {
    localStorage.setItem(BUSY_TABS_KEY, serializeBusyTabs(tabs));
  } catch {
    /* storage unavailable — this tab's own in-tab flag (below) is unaffected */
  }
}

/** This tab's id — shared with inboundTab.ts so "the active tab" and "the busy tabs" speak about the
 *  same tab. */
export function getTabId(): string {
  return tabId;
}

const selfActiveListeners = new Set<() => void>();

/** Fires when THIS tab's own call flag flips. The native `storage` event never fires in the tab that
 *  wrote, so inboundTab.ts needs this to re-evaluate "should I present?" when its own call starts
 *  or ends. */
export function onSelfCallActiveChange(cb: () => void): () => void {
  selfActiveListeners.add(cb);
  return () => selfActiveListeners.delete(cb);
}

// While this tab is on a call it re-reports itself busy on a timer, so other tabs can tell it from one that
// died without cleaning up — see crossTabBusy.logic.ts.
let heartbeat: ReturnType<typeof setInterval> | null = null;

function reportBusy(): void {
  writeBusyTabs(withBusyTab(readBusyTabs(), tabId, Date.now()));
}

export function setCallActive(v: boolean): void {
  const changed = active !== v;
  active = v;
  if (v) {
    reportBusy();
    if (!heartbeat) heartbeat = setInterval(reportBusy, BUSY_HEARTBEAT_MS);
  } else {
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = null;
    writeBusyTabs(withIdleTab(readBusyTabs(), tabId, Date.now()));
  }
  if (changed) selfActiveListeners.forEach((cb) => cb());
}

export function isCallActive(): boolean {
  return active;
}

// ⛔ 12 Oct 2026: an incoming call that is still RINGING is not "active" (no Call is accepted yet, so
// setCallActive was never raised) — but a reload at that moment kills the ring just as dead. A rep
// in the middle of picking up, with a deploy having landed, would have the page reload under the
// ringing call: it vanishes, the caller gets voicemail, and the rep sees nothing. AutoUpdate asks
// isCallInProgress(), which covers both.
let ringing = false;

export function setIncomingRinging(v: boolean): void {
  ringing = v;
}

/** A call is live OR an incoming one is ringing/waiting in this tab — anything a reload would destroy. */
export function isCallInProgress(): boolean {
  return active || ringing;
}

/** Whether some tab OTHER than this one currently has a call active, right now. */
export function isOtherTabBusyNow(): boolean {
  return isAnyOtherTabBusy(readBusyTabs(), tabId, Date.now());
}

/** Fires whenever another tab's busy state changes. Returns an unsubscribe function.
 *
 *  The native `storage` event only fires in tabs OTHER than the one that wrote the value — exactly
 *  what this needs, since a tab never has to hear about its own write, and it fires synchronously
 *  on the SAME microtask the write completed in every browser RT Hub targets, so there's no polling. */
export function onOtherTabBusyChange(cb: (busy: boolean) => void): () => void {
  let last = isOtherTabBusyNow();
  const handler = (e: StorageEvent) => {
    if (e.key !== null && e.key !== BUSY_TABS_KEY) return;
    last = isAnyOtherTabBusy(parseBusyTabs(e.newValue), tabId, Date.now());
    cb(last);
  };
  window.addEventListener('storage', handler);
  // ⛔ 13 Oct 2026: a tab that dies stops writing, so NO storage event announces that it is gone — the entry just
  // ages out. Re-read on a timer so a dead tab's mute lifts by itself instead of waiting for some unrelated event.
  const timer = setInterval(() => {
    const now = isOtherTabBusyNow();
    if (now !== last) {
      last = now;
      cb(now);
    }
  }, BUSY_HEARTBEAT_MS);
  return () => {
    window.removeEventListener('storage', handler);
    clearInterval(timer);
  };
}

/** Broadcasts that the call identified by `callSid` was just accepted on THIS tab, so every
 *  sibling tab currently ringing its own forked leg of the SAME inbound call can silence itself
 *  immediately. `callSid` must be the PARENT leg's id (the backend's custom TwiML parameter,
 *  `call.customParameters?.get('CallSid')` — see RtHubDialerProvider.tsx's wireCallHandlers
 *  comment), never `call.parameters.CallSid`, which is unique PER FORKED LEG and would never
 *  match across tabs. See onCallAnsweredElsewhere below for why this broadcast exists at all. */
export function broadcastCallAnswered(callSid: string): void {
  try {
    localStorage.setItem(CALL_ANSWERED_KEY, JSON.stringify({ callSid, ts: Date.now() }));
  } catch {
    /* storage unavailable — sibling tabs simply never hear about it, same silent no-op as the busy flag above */
  }
}

/** Fires in every OTHER tab the instant one tab accepts an incoming call, with that call's parent
 *  CallSid.
 *
 *  ⛔ 2 Oct 2026: Twilio rings every registered Device for an identity — same fact the busy flag
 *  above exists for — so each open tab gets its OWN forked Call object for the SAME inbound call.
 *  Accepting on one tab does NOT reliably stop the ringtone on the others: the Voice SDK only
 *  stops a Device's own incoming sound once THAT Device's OWN call object receives a terminal
 *  event (cancel/disconnect/reject) from Twilio's signaling (confirmed by reading
 *  node_modules/@twilio/voice-sdk/esm/twilio/device.js's `_maybeStopIncomingSound`, gated on
 *  `this._calls.length === 0`) — it has no built-in awareness that a SIBLING tab's device handled
 *  it. Reported live, Jacob relaying Jesse (BDM), 2 Oct 2026: "if you answer on 1 tab it will
 *  still ring on all tabs" — confirmed reproducible ("Yep exactly"). Rather than trust Twilio's
 *  cross-device cancel timing — which this exact bug report shows cannot be relied on — every tab
 *  broadcasts its own acceptance the instant it happens, and siblings react immediately by calling
 *  `Call.ignore()` on their own matching leg: `ignore()` DOES synchronously stop that Device's own
 *  incoming sound (device.js's `onIgnore` callback calls `_soundcache.get(Incoming).stop()`
 *  directly), with no signal sent back to Twilio (unlike `reject()`) — appropriate since the
 *  call's fate is already decided elsewhere. */
export function onCallAnsweredElsewhere(cb: (callSid: string) => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== null && e.key !== CALL_ANSWERED_KEY) return;
    const callSid = parseAnsweredCallSid(e.newValue);
    if (callSid) cb(callSid);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

// ⛔ A tab that closes mid-call (crash, force-quit, the whole browser closing) never reaches
// resetCallState()'s setCallActive(false) — without this, its tabId sits in the busy set forever,
// and every OTHER tab stays muted for a call that no longer exists. 'pagehide' fires reliably on
// both a normal close and a back/forward-cache eviction; unlike 'beforeunload' it doesn't block
// bfcache and needs no prior user gesture to register.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (active) writeBusyTabs(withIdleTab(readBusyTabs(), tabId, Date.now()));
  });
}
